'use client'

import { motion } from 'motion/react'
import { useLocale, useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'
import { useMeasure } from 'react-use'
import { useGetRestaurantReviewsQuery } from '@/api/restaurant/get-restaurant-reviews-query'
import { ExpandToggleButton } from '@/components/expand-toggle-button'
import { useRestaurantDetailContext } from '@/features/restaurants/restaurant-detail/restaurant-detail-context'
import { RestaurantDetailReviewCard } from '@/features/restaurants/restaurant-detail/restaurant-detail-review-card'
import { RestaurantDetailSection } from '@/features/restaurants/restaurant-detail/restaurant-detail-section'
import { RestaurantDetailSectionId } from '@/features/restaurants/restaurant-detail/utils/restaurant-detail-helpers'
import { Rating } from '@/features/reviews/rating'

const DEFAULT_NUMBER_OF_REVIEWS_SHOWN = 4

export const RestaurantDetailReviews = () => {
    const { restaurant } = useRestaurantDetailContext()

    const t = useTranslations()
    const locale = useLocale()

    const [isExpanded, setIsExpanded] = useState(false)

    const [containerRef, { height: containerHeight }] = useMeasure<HTMLDivElement>()

    const { data } = useGetRestaurantReviewsQuery({
        restaurantSlug: restaurant?.slug ?? '',
        page: 1,
    })

    const allReviews = data?.data

    const formattedReviews = useMemo(() => {
        if (!restaurant?.reviews) return

        const shownReviews = isExpanded && allReviews ? allReviews : restaurant.reviews

        return [...shownReviews]
            .sort(
                (a, b) => new Date(b.dateTimestamp).getTime() - new Date(a.dateTimestamp).getTime()
            )
            .map((review) => {
                return {
                    name: review.name || '',
                    rating: review.rating,
                    body: review.review ?? undefined,
                    dateTime: review.dateTimestamp as unknown as string,
                    userImageUrl: review.userImageUrl,
                    photos: review.photos?.filter((photo) => !photo.rejected),
                }
            })
    }, [restaurant, allReviews, isExpanded])

    const writtenReviewsCount = restaurant ? restaurant.reviewsCount : undefined

    const showExpandButton = useMemo(() => {
        return Boolean(writtenReviewsCount && writtenReviewsCount > DEFAULT_NUMBER_OF_REVIEWS_SHOWN)
    }, [writtenReviewsCount])

    if (!restaurant || typeof restaurant.avgRating !== 'number') return null

    return (
        <RestaurantDetailSection
            id={RestaurantDetailSectionId.REVIEWS}
            title={'restaurants.detail.reviews.title'}
            description={'restaurants.detail.reviews.description'}
        >
            <div className="flex flex-col gap-y-8 md:flex-row md:items-start md:gap-x-16 lg:gap-x-24">
                {/* Summary panel, stays in view below the tab bar while reading */}
                <div className="flex flex-col md:w-1/3 md:max-w-[360px] md:shrink-0 md:sticky md:top-20">
                    <span className="text-display-md">
                        {restaurant.avgRating.toLocaleString(locale, {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                        })}
                    </span>

                    <Rating
                        withReadableRating={false}
                        rating={restaurant.avgRating}
                        starsStyleType="maximal"
                        starClassName="w-6 h-6"
                        starsClassName="gap-[2px]"
                        ratingCountStyleType="only-count"
                        containerClassName="mt-1"
                    />

                    <p className="mt-2 text-p-sm text-fg-tertiary">
                        {restaurant.ratingsCount}{' '}
                        {t('restaurants.detail.reviews.ratingsLong', {
                            count: restaurant.reviewsCount,
                        })}
                    </p>
                </div>

                {/* Borderless review grid filling the remaining width */}
                <div className="min-w-0 flex-1">
                    {Boolean(formattedReviews?.length) && (
                        <motion.div
                            initial={{ height: '100%' }}
                            animate={{ height: containerHeight || '100%' }}
                            className="w-full overflow-hidden"
                        >
                            <div
                                ref={containerRef}
                                className="grid w-full grid-cols-1 lg:grid-cols-2 gap-x-12"
                            >
                                {formattedReviews?.map((review, index) => {
                                    const shouldRender =
                                        isExpanded || index < DEFAULT_NUMBER_OF_REVIEWS_SHOWN

                                    if (!shouldRender) return null

                                    return (
                                        <RestaurantDetailReviewCard
                                            review={review}
                                            key={'restaurant-detail-reviews' + index}
                                            className="border-t border-t-border-secondary py-8 first:border-t-0
                                                first:pt-0 last:pb-0 lg:[&:nth-child(-n+2)]:border-t-0
                                                lg:[&:nth-child(-n+2)]:pt-0"
                                        />
                                    )
                                })}
                            </div>
                        </motion.div>
                    )}

                    {showExpandButton && (
                        <ExpandToggleButton
                            isExpanded={isExpanded}
                            onClick={() => setIsExpanded((prev) => !prev)}
                        >
                            {isExpanded
                                ? t('restaurants.detail.reviews.showLess')
                                : t('restaurants.detail.reviews.showAll')}
                        </ExpandToggleButton>
                    )}
                </div>
            </div>
        </RestaurantDetailSection>
    )
}
